import classnames from 'classnames'
import { useState } from 'react'

import { Button, Carousel, IconArrowLeft, IconArrowRight, Image, Steps } from '@/components'
import { InverterManufacturer } from '@/models'

type TeslaIntegrationSetupGuideProps = {
    isCompleted?: boolean
    onComplete: () => void
}

const options = [
    {
        key: 'A',
        title: 'Share Site Via The Tesla One App',
        description: 'This option is only available during commissioning. If skipped, site access must be shared' +
            ' later via Powerhub.',
        content: <Carousel
            items={[
                <Image src="/assets/images/gifs/Option_A_Step_1.png" alt="Adding Partner Step 1"/>,
                <Image src="/assets/images/gifs/Option_A_Step_2.png" alt="Adding Partner Step 2"/>,
                <Image src="/assets/images/gifs/Option_A_Step_3.png" alt="Adding Partner Step 3"/>,
                <Image src="/assets/images/gifs/Option_A_Step_4.png" alt="Adding Partner Step 4"/>,
                <Image src="/assets/images/gifs/Option_A_Step_5.png" alt="Adding Partner Step 5"/>,
                <Image src="/assets/images/gifs/Option_A_Step_6.png" alt="Adding Partner Step 6"/>,
                <Image src="/assets/images/gifs/Option_A_Step_7.png" alt="Adding Partner Step 7"/>,
                <Image src="/assets/images/gifs/Option_A_Step_8.png" alt="Adding Partner Step 8"/>,
                <Image src="/assets/images/gifs/Option_A_Step_9.png" alt="Adding Partner Step 9"/>
            ]}
        />
    },
    {
        key: 'B',
        title: 'Share Site Via Powerhub',
        description: 'Only users with Owner or Advanced Support permissions can share site access via Powerhub.',
        content: <Carousel
            items={[
                <Image src="/assets/images/gifs/Option_B_Step_1.png" alt="Adding Partner Step 1"/>,
                <Image src="/assets/images/gifs/Option_B_Step_2.png" alt="Adding Partner Step 2"/>,
                <Image src="/assets/images/gifs/Option_B_Step_3.png" alt="Adding Partner Step 3"/>
            ]}
        />
    }
]

export const TeslaIntegrationSetupGuide = ({
    isCompleted,
    ...props
}: TeslaIntegrationSetupGuideProps) => {
    const [step, setStep] = useState(0)
    const [option, setOption] = useState(options[0])
    return <>
        <h3 className="text-gray-900 text-xl font-semibold flex gap-4">
            {InverterManufacturer.tesla.getLogo()}
            Tesla Integration Setup Instructions
        </h3>

        <p className="mt-6 text-sm text-gray-750">
            <span className="font-semibold">
                For every new Tesla install covered by a Solar Insure warranty, follow these two steps.
            </span>
            <br/>
            First, share tesla site data with Solar Insure via the Tesla One
            App <span className="italic">(during device commissioning)</span> or in
            Powerhub <span className="italic">(post device commissioning)</span>. Then, enroll the warranty in
            Daybreak with the correct system details to connect monitoring.
        </p>

        <Steps
            steps={[
                'Share Tesla Site Data with Solar Insure',
                'Enroll a Warranty In Daybreak'
            ]}
            step={step}
        />


        <div className="mt-7 5 bg-gray-25 border border-gray-200 px-5 py-6 rounded-xl">
            {step === 0 && <div className="grid lg:grid-cols-12 gap-4">
                <div className="lg:col-span-5 flex flex-col justify-center">
                    <h4 className="font-semibold text-gray-750 text-[15px]">
                        You can share site access in two ways - Choose the option that applies:
                    </h4>

                    <div className="mt-6 flex flex-col gap-4">
                        {options.map((item, index) =>
                            <button
                                key={item.key}
                                className={classnames(
                                    'cursor-pointer text-left flex gap-2.5 relative' +
                                    ' animate-fade-in',
                                    item.key === option.key ? 'text-primary-700' : 'text-gray-700'
                                )}
                                onClick={() => setOption(item)}
                            >
                                {index < options.length - 1 && <div
                                    className="w-0.5 absolute left-2.5 top-5.5 h-[calc(100%-8px)] bg-gray-200
                                         -translate-x-1/2"
                                />}
                                <div
                                    className={classnames(
                                        'w-5 h-5 rounded-full flex justify-center' +
                                        ' items-center shrink-0 transition-colors',
                                        { 'bg-primary-700 text-white': item.key === option.key }
                                    )}
                                >
                                    {item.key}
                                </div>
                                <div>
                                    <h5 className="text-xs font-semibold">{item.title}</h5>
                                    <p className="text-[11px] italic">{item.description}</p>
                                </div>
                            </button>)}
                    </div>
                </div>
                <div className="lg:col-span-7 animate-fade-in" key={option.key}>
                    {option.content}
                </div>
            </div>}
            {step === 1 && <Image
                src="/assets/images/gifs/tesla-warranty-enrollment-guide.gif"
                alt="Warranty Enrollment in Daybreak"
            />}
        </div>

        <div className="flex gap-3 justify-end mt-4">
            {step > 0 && <Button
                design="btn-link"
                type="button"
                onClick={() => setStep(0)}
                className="gap-2"
            >
                <IconArrowLeft/>
                Back
            </Button>}
            {step > 0
                ? <Button
                    type="button"
                    design="btn-secondary"
                    onClick={() => {
                        props.onComplete()
                        setStep(0)
                    }}
                    key="complete"
                >
                    Complete Instructions
                </Button>
                : <Button
                    type="button"
                    design="btn-secondary"
                    onClick={() => setStep(1)}
                    className="gap-2"
                    key="next"
                >
                    Continue
                    <IconArrowRight/>
                </Button>}
        </div>
    </>
}
